/**
 * Comment Component
 *
 * Renders a forum comment with options, post content, user response, and answers.
 * Handles editing, showing responses, and fetching answers asynchronously.
 *
 * Features:
 * - Displays comment options for admin and regular users
 * - Allows toggling edit mode for the comment
 * - Handles user responses and displays subcomments
 * - Fetches and displays answers to the comment
 * - Passes relevant props to child components for comment data and actions
 * - Uses CSS modules for scoped styling
 *
 * @param {Object} props - Component properties
 * @param {Object} [props.commentData] - Data object for the comment
 * @param {number} [props.idLesson] - Lesson ID associated with the comment
 * @param {number} [props.idCourse] - Course ID associated with the comment
 * @returns {JSX.Element} Rendered Comment component
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import CommentPost from '../CommentPost/CommentPost';
import UserComment from '../UserComment/UserComment';
import AnswerComment from '../AnswerComment/AnswerComment';
import { getAnswer } from 'store/lesson-actions';
import CommentOptions from '../CommentOptions/CommentOptions';
import classes from './CommentContainer.module.css';

const Comment = props => {
  const [responseComment, setResponseComment] = useState(false);
  const [editComment, setEditComment] = useState(false);
  const [answers, setAnswers] = useState([]);

  /**
   * Toggles the response comment input
   */
  const showResponseCommentHandler = () => {
    setResponseComment(!responseComment);
  };

  /**
   * Fetches answers for the current comment asynchronously
   */
  const showAnswersHandler = () => {
    (async () => {
      const answers = await getAnswer(
        props.commentData.idComment,
        'es',
        true
      );
      setAnswers(answers);
    })();
  };

  /**
   * Toggles the edit mode for the comment
   */
  const editCommentHandler = () => {
    setEditComment(!editComment);
  };

  return (
    <div className={classes.commentContainer}>
      {/* Custom component: CommentOptions receives adminComment, idComment, idLesson, editCommentHandler */}
      <CommentOptions
        adminComment={props.commentData.admin === 1 ? true : false}
        idComment={props.commentData.idComment}
        idLesson={props.idLesson}
        editCommentHandler={editCommentHandler}
      />
      {/* Custom component: CommentPost receives responseComment, editCommentHandler, editComment, commentData, showAnswers, idComment */}
      <CommentPost
        responseComment={showResponseCommentHandler}
        editCommentHandler={editCommentHandler}
        editComment={editComment}
        commentData={props.commentData}
        showAnswers={showAnswersHandler}
        idComment={props.commentData.idComment}
      />
      <div className={classes.responseContainer}>
        {responseComment && (
          /* Custom component: UserComment receives subcomment, idComment, showAnswers, idLesson, idCourse */
          <UserComment
            subcomment
            idComment={props.commentData.idComment}
            showAnswers={showAnswersHandler}
            idLesson={props.idLesson}
            idCourse={props.idCourse}
          />
        )}
        {answers.map(answer => (
          /* Custom component: AnswerComment receives answerData, idLesson, showAnswers */
          <AnswerComment
            key={answer.idSubcomment}
            answerData={answer}
            idLesson={props.idLesson}
            showAnswers={showAnswersHandler}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
Comment.propTypes = {
  /** Data object for the comment */
  commentData: PropTypes.object,
  /** Lesson ID associated with the comment */
  idLesson: PropTypes.number,
  /** Course ID associated with the comment */
  idCourse: PropTypes.number
};

Comment.defaultProps = {
  commentData: undefined,
  idLesson: undefined,
  idCourse: undefined
};

export default Comment;
